/**
 * Pure helpers for Image Editor transient float (single uncommitted object).
 * Not a layer stack — at most one float; buffer untouched until bake/discard.
 */

/**
 * @typedef {{
 *   text: string,
 *   bold?: boolean,
 *   italic?: boolean,
 *   color?: string,
 *   fontFamily?: string,
 *   fontSize?: number,
 * }} EditorTextRun
 */

/**
 * @typedef {{
 *   text: string,
 *   fontFamily: string,
 *   fontSize: number,
 *   color: string,
 *   align: 'left'|'center'|'right',
 *   bold?: boolean,
 *   italic?: boolean,
 *   runs?: EditorTextRun[],
 * }} EditorTextParams
 */

/**
 * @typedef {{
 *   imageData: ImageData,
 *   cx: number,
 *   cy: number,
 *   scale: number,
 *   scaleX: number,
 *   scaleY: number,
 *   rotationDeg: number,
 *   width: number,
 *   height: number,
 *   origin: 'paste'|'import'|'lift'|'transform-canvas'|'generate'|'img2img'|'inpaint'|'text'|'path',
 *   labelHint?: string,
 *   textParams?: EditorTextParams,
 * }} EditorFloat
 */

export const FLOAT_MIN_SCALE = 0.05;
export const FLOAT_MAX_SCALE = 8;
export const FLOAT_HANDLE_HIT_PX = 10;

/**
 * @param {ImageData} imageData
 * @returns {ImageData}
 */
export function cloneImageData(imageData) {
  return new ImageData(
    new Uint8ClampedArray(imageData.data),
    imageData.width,
    imageData.height,
  );
}

/**
 * @param {number} scale
 * @returns {number}
 */
export function clampFloatScale(scale) {
  const s = Number(scale);
  if (!Number.isFinite(s) || s <= 0) return 1;
  return Math.min(FLOAT_MAX_SCALE, Math.max(FLOAT_MIN_SCALE, s));
}

/**
 * Resolve independent scale axes. Legacy single `scale` migrates to
 * scaleX = scaleY = scale. Explicit scaleX/scaleY win when present.
 *
 * @param {{
 *   scale?: number,
 *   scaleX?: number,
 *   scaleY?: number,
 * }|null|undefined} src
 * @returns {{ scaleX: number, scaleY: number, scale: number }}
 */
export function resolveFloatScaleAxes(src) {
  const legacy = src && src.scale != null ? clampFloatScale(src.scale) : 1;
  const scaleX =
    src && src.scaleX != null ? clampFloatScale(src.scaleX) : legacy;
  const scaleY =
    src && src.scaleY != null ? clampFloatScale(src.scaleY) : legacy;
  // Keep `scale` as the uniform alias when axes match; otherwise prefer
  // scaleX for legacy Scale UI / older readers (non-uniform arrives in Slice 2).
  const scale = scaleX === scaleY ? scaleX : scaleX;
  return { scaleX, scaleY, scale };
}

/**
 * Apply a uniform scale to both axes (corner-handle / Scale field).
 * @param {EditorFloat} float
 * @param {number} nextScale
 * @returns {EditorFloat}
 */
export function withUniformFloatScale(float, nextScale) {
  const s = clampFloatScale(nextScale);
  return {
    ...float,
    scale: s,
    scaleX: s,
    scaleY: s,
  };
}

/**
 * Write scale axes onto a float, keeping legacy `scale` in sync when uniform.
 * @param {EditorFloat} float
 * @param {number} scaleX
 * @param {number} scaleY
 * @returns {EditorFloat}
 */
export function withFloatScaleAxes(float, scaleX, scaleY) {
  const sx = clampFloatScale(scaleX);
  const sy = clampFloatScale(scaleY);
  return {
    ...float,
    scaleX: sx,
    scaleY: sy,
    scale: sx === sy ? sx : sx,
  };
}

/**
 * Uniform scale so a float's natural size fits inside the document with
 * room for transform handles (corners / edge mids / rotate) on-canvas.
 * Does not upscale past 1 — small imports stay 1:1.
 *
 * @param {number} naturalW
 * @param {number} naturalH
 * @param {number} docW
 * @param {number} docH
 * @param {{ padPx?: number, maxScale?: number }} [opts]
 * @returns {number}
 */
export function fitFloatScaleToDocument(
  naturalW,
  naturalH,
  docW,
  docH,
  opts = {},
) {
  const pad = Number.isFinite(opts.padPx) ? Math.max(0, opts.padPx) : 36;
  const maxScale = Number.isFinite(opts.maxScale)
    ? opts.maxScale
    : 1;
  const usableW = Math.max(1, Number(docW) - 2 * pad);
  const usableH = Math.max(1, Number(docH) - 2 * pad);
  const sx = usableW / Math.max(1, Number(naturalW) || 1);
  const sy = usableH / Math.max(1, Number(naturalH) || 1);
  return clampFloatScale(Math.min(maxScale, sx, sy));
}

/**
 * @param {number} deg
 * @returns {number}
 */
export function normalizeRotationDeg(deg) {
  const d = Number(deg);
  if (!Number.isFinite(d)) return 0;
  let n = d % 360;
  if (n > 180) n -= 360;
  if (n <= -180) n += 360;
  return n;
}

/**
 * @param {{
 *   imageData: ImageData,
 *   cx?: number,
 *   cy?: number,
 *   scale?: number,
 *   scaleX?: number,
 *   scaleY?: number,
 *   rotationDeg?: number,
 *   origin?: EditorFloat['origin'],
 * }} params
 * @returns {EditorFloat}
 */
export function createFloat(params) {
  const imageData = params.imageData;
  const width = imageData.width;
  const height = imageData.height;
  const axes = resolveFloatScaleAxes(params);
  /** @type {EditorFloat} */
  const float = {
    imageData: cloneImageData(imageData),
    cx: Number.isFinite(params.cx) ? params.cx : width / 2,
    cy: Number.isFinite(params.cy) ? params.cy : height / 2,
    scale: axes.scale,
    scaleX: axes.scaleX,
    scaleY: axes.scaleY,
    rotationDeg: normalizeRotationDeg(
      params.rotationDeg == null ? 0 : params.rotationDeg,
    ),
    width,
    height,
    origin: params.origin || 'paste',
  };
  if (typeof params.labelHint === 'string' && params.labelHint.trim()) {
    float.labelHint = params.labelHint.trim().slice(0, 48);
  }
  if (params.textParams && typeof params.textParams === 'object') {
    const tp = params.textParams;
    /** @type {EditorTextParams} */
    const textParams = {
      text: String(tp.text || ''),
      fontFamily: String(tp.fontFamily || 'Arial, Helvetica, sans-serif'),
      fontSize: Math.max(
        8,
        Math.min(256, Math.round(Number(tp.fontSize) || 32)),
      ),
      color: String(tp.color || '#000000'),
      align:
        tp.align === 'center' || tp.align === 'right' ? tp.align : 'left',
      bold: Boolean(tp.bold),
      italic: Boolean(tp.italic),
    };
    if (Array.isArray(tp.runs)) {
      textParams.runs = tp.runs.map((run) => {
        const src = run && typeof run === 'object' ? run : {};
        /** @type {EditorTextRun} */
        const out = { text: String(src.text || '') };
        if (src.bold !== undefined) out.bold = Boolean(src.bold);
        if (src.italic !== undefined) out.italic = Boolean(src.italic);
        if (src.color !== undefined) out.color = String(src.color);
        if (src.fontFamily !== undefined) out.fontFamily = String(src.fontFamily);
        if (src.fontSize !== undefined) {
          out.fontSize = Math.max(
            8,
            Math.min(256, Math.round(Number(src.fontSize) || 32)),
          );
        }
        return out;
      });
    }
    float.textParams = textParams;
  }
  return float;
}

/**
 * Local-space corners relative to center (after scaleX/scaleY).
 * @param {EditorFloat} float
 * @returns {Array<{x:number,y:number,id:string}>}
 */
export function floatLocalCorners(float) {
  const { scaleX, scaleY } = resolveFloatScaleAxes(float);
  const hw = (float.width * scaleX) / 2;
  const hh = (float.height * scaleY) / 2;
  return [
    { id: 'nw', x: -hw, y: -hh },
    { id: 'ne', x: hw, y: -hh },
    { id: 'se', x: hw, y: hh },
    { id: 'sw', x: -hw, y: hh },
  ];
}

/**
 * Local-space edge midpoints (n/e/s/w) relative to center.
 * @param {EditorFloat} float
 * @returns {Array<{x:number,y:number,id:'n'|'e'|'s'|'w'}>}
 */
export function floatLocalEdgeMidpoints(float) {
  const { scaleX, scaleY } = resolveFloatScaleAxes(float);
  const hw = (float.width * scaleX) / 2;
  const hh = (float.height * scaleY) / 2;
  return [
    { id: 'n', x: 0, y: -hh },
    { id: 'e', x: hw, y: 0 },
    { id: 's', x: 0, y: hh },
    { id: 'w', x: -hw, y: 0 },
  ];
}

/**
 * @param {number} x
 * @param {number} y
 * @param {number} deg
 * @returns {{x:number,y:number}}
 */
export function rotatePoint(x, y, deg) {
  const rad = (deg * Math.PI) / 180;
  const c = Math.cos(rad);
  const s = Math.sin(rad);
  return { x: x * c - y * s, y: x * s + y * c };
}

/**
 * World-space (canvas) corners after transform.
 * @param {EditorFloat} float
 * @returns {Array<{x:number,y:number,id:string}>}
 */
export function floatWorldCorners(float) {
  return floatLocalCorners(float).map((corner) => {
    const rotated = rotatePoint(corner.x, corner.y, float.rotationDeg);
    return {
      id: corner.id,
      x: float.cx + rotated.x,
      y: float.cy + rotated.y,
    };
  });
}

/**
 * World-space edge midpoints after rotation.
 * @param {EditorFloat} float
 * @returns {Array<{x:number,y:number,id:'n'|'e'|'s'|'w'}>}
 */
export function floatWorldEdgeMidpoints(float) {
  return floatLocalEdgeMidpoints(float).map((edge) => {
    const rotated = rotatePoint(edge.x, edge.y, float.rotationDeg);
    return {
      id: edge.id,
      x: float.cx + rotated.x,
      y: float.cy + rotated.y,
    };
  });
}

/**
 * Rotation handle sits above the top edge center in local space.
 * @param {EditorFloat} float
 * @param {number} [offsetPx]
 * @returns {{x:number,y:number}}
 */
export function floatRotationHandle(float, offsetPx = 28) {
  const { scaleY } = resolveFloatScaleAxes(float);
  const localY = -(float.height * scaleY) / 2 - offsetPx;
  const rotated = rotatePoint(0, localY, float.rotationDeg);
  return { x: float.cx + rotated.x, y: float.cy + rotated.y };
}

/**
 * @param {EditorFloat} float
 * @returns {{minX:number,minY:number,maxX:number,maxY:number}}
 */
export function floatAABB(float) {
  const corners = floatWorldCorners(float);
  let minX = Infinity;
  let minY = Infinity;
  let maxX = -Infinity;
  let maxY = -Infinity;
  corners.forEach((c) => {
    minX = Math.min(minX, c.x);
    minY = Math.min(minY, c.y);
    maxX = Math.max(maxX, c.x);
    maxY = Math.max(maxY, c.y);
  });
  return { minX, minY, maxX, maxY };
}

/**
 * Point-in-OBB using inverse rotation.
 * @param {EditorFloat} float
 * @param {number} canvasX
 * @param {number} canvasY
 * @returns {boolean}
 */
export function pointInFloat(float, canvasX, canvasY) {
  const dx = canvasX - float.cx;
  const dy = canvasY - float.cy;
  const local = rotatePoint(dx, dy, -float.rotationDeg);
  const { scaleX, scaleY } = resolveFloatScaleAxes(float);
  const hw = (float.width * scaleX) / 2;
  const hh = (float.height * scaleY) / 2;
  return local.x >= -hw && local.x <= hw && local.y >= -hh && local.y <= hh;
}

/**
 * @param {EditorFloat} float
 * @param {number} canvasX
 * @param {number} canvasY
 * @param {number} [hitPx] canvas-space hit radius
 * @returns {'move'|'rotate'|'nw'|'ne'|'se'|'sw'|'n'|'e'|'s'|'w'|null}
 */
export function hitTestFloatHandle(float, canvasX, canvasY, hitPx = FLOAT_HANDLE_HIT_PX) {
  /** @type {Array<{id:string,x:number,y:number}>} */
  const handles = [
    { id: 'rotate', ...floatRotationHandle(float) },
    ...floatWorldCorners(float),
    ...floatWorldEdgeMidpoints(float),
  ];
  let bestId = null;
  let bestDist = Infinity;
  for (let i = 0; i < handles.length; i += 1) {
    const h = handles[i];
    const dist = Math.hypot(canvasX - h.x, canvasY - h.y);
    if (dist <= hitPx && dist < bestDist) {
      bestDist = dist;
      bestId = h.id;
    }
  }
  if (bestId) {
    // Edge mids on short floats can sit within hitPx of the center; keep
    // body-move when the pointer is closer to the center than to the handle.
    if (
      (bestId === 'n' ||
        bestId === 'e' ||
        bestId === 's' ||
        bestId === 'w') &&
      bestDist >= Math.hypot(canvasX - float.cx, canvasY - float.cy)
    ) {
      // fall through to body / null
    } else {
      return /** @type {any} */ (bestId);
    }
  }
  if (pointInFloat(float, canvasX, canvasY)) return 'move';
  return null;
}

/**
 * Whether a pointer interaction should commit the active float instead of
 * starting a transform drag.
 *
 * Rule: hit on the float (body/handle/edit surface) → do not commit;
 * outside → commit. In-canvas text edit (contentEditable) counts as a hit
 * via data-float-chrome / editSurfaceHit so caret clicks never commit.
 *
 * @param {EditorFloat|null|undefined} float
 * @param {{ hit: string|null, textOptionsFocused?: boolean, editSurfaceHit?: boolean }} opts
 * @returns {boolean}
 */
export function shouldCommitFloatOnPointer(float, opts) {
  if (!float) return false;
  if (opts?.editSurfaceHit) return false;
  return !opts.hit;
}

/**
 * Uniform scale from dragging a corner, preserving aspect.
 * Returns a single scale applied to both axes (Slice 1 / corner handles).
 * @param {EditorFloat} start
 * @param {'nw'|'ne'|'se'|'sw'} cornerId
 * @param {number} canvasX
 * @param {number} canvasY
 * @returns {number}
 */
export function scaleFromCornerDrag(start, cornerId, canvasX, canvasY) {
  const dx = canvasX - start.cx;
  const dy = canvasY - start.cy;
  const local = rotatePoint(dx, dy, -start.rotationDeg);
  const halfW = start.width / 2;
  const halfH = start.height / 2;
  if (halfW < 1 || halfH < 1) {
    return resolveFloatScaleAxes(start).scale;
  }
  // Distance from center to corner in local space vs natural half-diagonal axis.
  const sx = Math.abs(local.x) / halfW;
  const sy = Math.abs(local.y) / halfH;
  const next = Math.max(sx, sy);
  return clampFloatScale(next);
}

/**
 * Non-uniform edge stretch in local (unrotated) object space.
 * Anchors the opposite edge: 'e' keeps left fixed, 'w' keeps right, 's'
 * keeps top, 'n' keeps bottom. Updates scaleX/scaleY and recenters so the
 * anchored edge stays fixed in world space (works under rotation).
 *
 * @param {EditorFloat} start
 * @param {'n'|'e'|'s'|'w'} edgeId
 * @param {number} canvasX
 * @param {number} canvasY
 * @returns {EditorFloat}
 */
export function floatFromEdgeDrag(start, edgeId, canvasX, canvasY) {
  const axes = resolveFloatScaleAxes(start);
  const dx = canvasX - start.cx;
  const dy = canvasY - start.cy;
  const local = rotatePoint(dx, dy, -start.rotationDeg);
  const w = Math.max(1, start.width);
  const h = Math.max(1, start.height);
  const hw0 = (w * axes.scaleX) / 2;
  const hh0 = (h * axes.scaleY) / 2;

  let scaleX = axes.scaleX;
  let scaleY = axes.scaleY;
  /** New center offset in start-local space (before rotation). */
  let ocx = 0;
  let ocy = 0;

  if (edgeId === 'e') {
    scaleX = clampFloatScale((local.x + hw0) / w);
    ocx = (w * scaleX) / 2 - hw0;
  } else if (edgeId === 'w') {
    scaleX = clampFloatScale((hw0 - local.x) / w);
    ocx = hw0 - (w * scaleX) / 2;
  } else if (edgeId === 's') {
    scaleY = clampFloatScale((local.y + hh0) / h);
    ocy = (h * scaleY) / 2 - hh0;
  } else if (edgeId === 'n') {
    scaleY = clampFloatScale((hh0 - local.y) / h);
    ocy = hh0 - (h * scaleY) / 2;
  } else {
    return { ...start };
  }

  const world = rotatePoint(ocx, ocy, start.rotationDeg);
  return {
    ...withFloatScaleAxes(start, scaleX, scaleY),
    cx: start.cx + world.x,
    cy: start.cy + world.y,
  };
}

/**
 * World-space position of an edge midpoint (for anchor assertions).
 * @param {EditorFloat} float
 * @param {'n'|'e'|'s'|'w'} edgeId
 * @returns {{x:number,y:number}|null}
 */
export function floatWorldEdgePoint(float, edgeId) {
  const edges = floatWorldEdgeMidpoints(float);
  const found = edges.find((e) => e.id === edgeId);
  return found ? { x: found.x, y: found.y } : null;
}

/**
 * Rotation degrees from pointer relative to float center.
 * @param {number} cx
 * @param {number} cy
 * @param {number} canvasX
 * @param {number} canvasY
 * @returns {number}
 */
export function rotationDegFromPointer(cx, cy, canvasX, canvasY) {
  const rad = Math.atan2(canvasY - cy, canvasX - cx);
  // Handle sits "above" center at -90° in local space when rotation=0.
  return normalizeRotationDeg((rad * 180) / Math.PI + 90);
}

/**
 * Bake float onto a 2D context (working canvas). Does not push history.
 * @param {CanvasRenderingContext2D} ctx
 * @param {EditorFloat} float
 */
/**
 * Rasterize a float onto a transparent document-sized ImageData.
 * @param {EditorFloat} float
 * @param {number} width
 * @param {number} height
 * @returns {ImageData|null}
 */
export function rasterizeFloatToDocumentPixels(float, width, height) {
  if (!float?.imageData || typeof document === 'undefined') return null;
  const canvas = document.createElement('canvas');
  canvas.width = width;
  canvas.height = height;
  const ctx = canvas.getContext('2d', { willReadFrequently: true });
  if (!ctx) return null;
  ctx.clearRect(0, 0, width, height);
  const temp = document.createElement('canvas');
  temp.width = float.width;
  temp.height = float.height;
  const tctx = temp.getContext('2d');
  if (!tctx) return null;
  tctx.putImageData(float.imageData, 0, 0);
  ctx.save();
  ctx.translate(float.cx, float.cy);
  ctx.rotate((float.rotationDeg * Math.PI) / 180);
  {
    const { scaleX, scaleY } = resolveFloatScaleAxes(float);
    ctx.scale(scaleX, scaleY);
  }
  ctx.drawImage(temp, -float.width / 2, -float.height / 2);
  ctx.restore();
  return ctx.getImageData(0, 0, width, height);
}

export function bakeFloatOntoContext(ctx, float) {
  if (!ctx || !float?.imageData) return;
  const temp = document.createElement('canvas');
  temp.width = float.width;
  temp.height = float.height;
  const tctx = temp.getContext('2d');
  if (!tctx) return;
  tctx.putImageData(float.imageData, 0, 0);
  ctx.save();
  ctx.translate(float.cx, float.cy);
  ctx.rotate((float.rotationDeg * Math.PI) / 180);
  {
    const { scaleX, scaleY } = resolveFloatScaleAxes(float);
    ctx.scale(scaleX, scaleY);
  }
  ctx.drawImage(temp, -float.width / 2, -float.height / 2);
  ctx.restore();
}

/**
 * Crop document-sized layer pixels to non-transparent bounds for re-transform.
 * Returns natural-size ImageData plus document-space center of that crop.
 *
 * @param {ImageData} imageData
 * @returns {{ imageData: ImageData, cx: number, cy: number }|null}
 */
export function extractOpaqueContentFloat(imageData) {
  if (!imageData?.data || !imageData.width || !imageData.height) return null;
  const w = imageData.width;
  const h = imageData.height;
  const d = imageData.data;
  let minX = w;
  let minY = h;
  let maxX = -1;
  let maxY = -1;
  for (let y = 0; y < h; y += 1) {
    const row = y * w;
    for (let x = 0; x < w; x += 1) {
      if (d[(row + x) * 4 + 3] > 0) {
        if (x < minX) minX = x;
        if (y < minY) minY = y;
        if (x > maxX) maxX = x;
        if (y > maxY) maxY = y;
      }
    }
  }
  if (maxX < minX || maxY < minY) return null;
  const cw = maxX - minX + 1;
  const ch = maxY - minY + 1;
  const out = new ImageData(cw, ch);
  const od = out.data;
  for (let y = 0; y < ch; y += 1) {
    for (let x = 0; x < cw; x += 1) {
      const si = ((minY + y) * w + (minX + x)) * 4;
      const di = (y * cw + x) * 4;
      od[di] = d[si];
      od[di + 1] = d[si + 1];
      od[di + 2] = d[si + 2];
      od[di + 3] = d[si + 3];
    }
  }
  return {
    imageData: out,
    cx: minX + cw / 2,
    cy: minY + ch / 2,
  };
}

/**
 * True when a raster layer has a visible (alpha > 0) pixel at canvas coords.
 * @param {{ pixels?: ImageData|null }|null|undefined} layer
 * @param {number} canvasX
 * @param {number} canvasY
 * @returns {boolean}
 */
export function hitTestRasterLayerPixel(layer, canvasX, canvasY) {
  const px = layer?.pixels;
  if (!px?.data) return false;
  const x = Math.round(canvasX);
  const y = Math.round(canvasY);
  if (x < 0 || y < 0 || x >= px.width || y >= px.height) return false;
  return px.data[(y * px.width + x) * 4 + 3] > 0;
}

/**
 * Put ImageData onto an offscreen canvas (for overlay preview).
 * @param {ImageData} imageData
 * @returns {HTMLCanvasElement|null}
 */
export function imageDataToCanvas(imageData) {
  if (!imageData) return null;
  const canvas = document.createElement('canvas');
  canvas.width = imageData.width;
  canvas.height = imageData.height;
  const ctx = canvas.getContext('2d');
  if (!ctx) return null;
  ctx.putImageData(imageData, 0, 0);
  return canvas;
}

/**
 * Snap float center so AABB edges prefer grid/guides (axis-aligned approx).
 * @param {EditorFloat} float
 * @param {{
 *   snapEnabled: boolean,
 *   showGrid: boolean,
 *   gridSize: number,
 *   vGuides: number[],
 *   hGuides: number[],
 *   width: number,
 *   height: number,
 *   snapAxisValue: Function,
 * }} opts
 * @returns {EditorFloat}
 */
export function snapFloatTranslation(float, opts) {
  if (!opts?.snapEnabled || typeof opts.snapAxisValue !== 'function') {
    return float;
  }
  const box = floatAABB(float);
  const left = opts.snapAxisValue(box.minX, {
    snapEnabled: true,
    showGrid: opts.showGrid,
    gridSize: opts.gridSize,
    guides: opts.vGuides,
    max: opts.width - 1,
  });
  const top = opts.snapAxisValue(box.minY, {
    snapEnabled: true,
    showGrid: opts.showGrid,
    gridSize: opts.gridSize,
    guides: opts.hGuides,
    max: opts.height - 1,
  });
  const dx = left - box.minX;
  const dy = top - box.minY;
  if (!dx && !dy) return float;
  return { ...float, cx: float.cx + dx, cy: float.cy + dy };
}
